import ToggleIconButton from '@naturehouse/design-system/components/molecules/toggle-icon-button/ToggleIconButton';
import HttpClient from '@naturehouse/nh-essentials/lib/requests/HttpClient';
import GTM from '../../../gtm/GTM';
import GTMAddToWishlistModel from '../../../gtm/models/GTMAddToWishlistModel';
import LoginDialog, { LoginState } from '../LoginDialog';
import '../../../../css/components/_favoriteAdd.pcss';

export interface FavoriteMetaDataType {
    id: string;
}

export default class AddFavoriteButton extends ToggleIconButton {
    readonly #favorited: boolean = this.hasAttribute('favorited');

    readonly #endpoint: string = this.getAttribute('endpoint') ?? '';

    public connectedCallback(): void {
        if (!this.#endpoint) {
            return;
        }

        super.connectedCallback();

        this.classList.toggle('disabled', this.#favorited);

        this.addEventListener('click', this.#handleClick.bind(this));
    }

    public disconnectedCallback(): void {
        super.disconnectedCallback();
        this.removeEventListener('click', this.#handleClick.bind(this));
    }

    async #handleClick(): Promise<void> {
        const successCallback = async (): Promise<void> => {
            await this.#toggleFavorite();
        };

        if (document.body.classList.contains(LoginState.USER_AVAILABLE)) {
            await successCallback();
            return;
        }

        const loginDialog = LoginDialog.getInstance();

        loginDialog.successCallback = successCallback;
        loginDialog.show();
    }

    async #toggleFavorite(): Promise<void> {
        this.loading = true;

        const response = await HttpClient.post(this.#endpoint);
        const favoriteResponse = response as unknown as FavoriteResponse;

        if (!favoriteResponse.success && favoriteResponse.notLoggedIn) {
            this.loading = false;
            const loginDialog = LoginDialog.getInstance();
            loginDialog.show();
            return;
        }

        if (!favoriteResponse.success) {
            return;
        }

        this.#trackUserFavorite(favoriteResponse);
        this.active = Boolean(favoriteResponse.is_favorite);
        this.loading = false;
    }

    #trackUserFavorite(response: FavoriteResponse): void {
        if (!response.is_favorite) {
            return;
        }

        this.#trackUsingMetaData();
    }

    #trackUsingMetaData(): void {
        let metaData: Partial<FavoriteMetaDataType> = {};
        try {
            metaData = JSON.parse(this.dataset.meta ?? '{}') as Partial<FavoriteMetaDataType>;
        } catch {
            return;
        }

        if (!metaData || !metaData.id) {
            return;
        }

        GTM.trackEcommerce(
            GTMAddToWishlistModel.parseToGTMData({
                id: `${metaData.id}`
            })
        );
    }
}

if (!customElements.get('nh-add-favorite')) {
    customElements.define('nh-add-favorite', AddFavoriteButton, { extends: 'div' });
}
